import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { X, Send, BookOpen, Map, Route } from "lucide-react";
import { chatSend } from "../api";
import { useLang } from "../LangContext";
import { API } from "../api";
import {
  buildContextualSuggestions,
  buildItineraryPrefill,
  resolveChatPageContext,
  withChatGeoPayload,
} from "../utils/chatPageContext";

const pubAxios = axios.create({ baseURL: API, timeout: 5000 });

function sid() {
  let s = localStorage.getItem("6amico_chat_sid");
  if (!s) {
    s = "6amico-" + Math.random().toString(36).slice(2) + "-" + Date.now();
    localStorage.setItem("6amico_chat_sid", s);
  }
  return s;
}

function chatErrorMessage(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (status === 503) {
    return "L'Ambasciatore è temporaneamente non disponibile. Prova a chiedere il nome di un borgo (es. Campli, Castelli) o riprova tra poco.";
  }
  if (status === 500) {
    return "Servizio Storico non disponibile al momento. Apri la scheda del comune dal menu Borghi.";
  }
  if (status === 401 || status === 403) {
    return "Ambasciatore temporaneamente non disponibile (autenticazione servizio). Riprova tra poco.";
  }
  return err?.message || "Errore di connessione. Riprova.";
}

export default function ChatWidget() {
  const { lang, t } = useLang();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [welcome, setWelcome] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const pageCtx = useMemo(
    () => resolveChatPageContext(location.pathname, searchParams),
    [location.pathname, searchParams],
  );

  const suggestions = useMemo(
    () => buildContextualSuggestions(pageCtx, lang),
    [pageCtx, lang],
  );

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("6amico:open-ambasciatore", onOpen);
    return () => window.removeEventListener("6amico:open-ambasciatore", onOpen);
  }, []);

  // Load instant welcome on first open (cached per lang)
  useEffect(() => {
    if (!open) return;
    if (welcome && welcome.lang === lang) return;
    pubAxios.get("/chat/welcome", { params: { lang } })
      .then(r => setWelcome(r.data))
      .catch(() => setWelcome(null));
  }, [open, lang, welcome]);

  function geoPayload(message) {
    return withChatGeoPayload(
      { session_id: sid(), message, language: lang },
      pageCtx,
    );
  }

  async function send(text) {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setSending(true);
    const payload = geoPayload(msg);
    try {
      // Solo POST /chat (archivio locale + Qwen). Mai /chat/stream: in free-mode
      // senza EMERGENT restituiva "Stream HTTP 503" e confondeva l'utente.
      const res = await chatSend(payload);
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: res.reply };
        return copy;
      });
    } catch (e) {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠ " + chatErrorMessage(e) };
        return copy;
      });
    } finally {
      setSending(false);
    }
  }

  function fillItinerary() {
    const prefill = buildItineraryPrefill(pageCtx);
    setInput(prefill);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(prefill.length, prefill.length);
    });
  }

  return (
    <>
      {open && (
        <div className="fixed bottom-4 right-4 z-[50] w-chat-panel h-chat-panel max-h-[min(32rem,calc(100vh-6rem))] bg-parchment rounded-2xl shadow-2xl border border-borderline flex flex-col overflow-hidden md:bottom-5 md:right-5" data-testid="chat-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-borderline bg-ink text-parchment">
            <div className="flex items-center gap-2.5 min-w-0">
              <BookOpen size={18} className="text-gold shrink-0" />
              <div className="min-w-0">
                <div className="font-display text-lg leading-none">{t.sections.chat}</div>
                <div className="text-[10px] font-mono tracking-widest uppercase text-gold/80 mt-1 truncate">
                  Qwen 3 · Ambasciatore gratis
                  {pageCtx.name ? ` · ${pageCtx.name}` : ""}
                </div>
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-parchment/80 hover:text-gold shrink-0">
              <X size={20} />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="chatbox-storico-container flex-1 overflow-y-auto p-4 space-y-3 bg-parchment paper-texture"
          >
            {messages.length === 0 && (
              <div className="pt-2">
                {welcome && (
                  <div
                    data-testid="chat-welcome-bubble"
                    className="bubble-ai chatbox-inner-bg rounded-2xl text-sm leading-relaxed mb-3"
                  >
                    <p className="chatbox-body-text chatbox-body-text--title font-display text-base mb-2">{welcome.title}</p>
                    {welcome.body.split('\n').map((line, j) => (
                      <p key={j} className="chatbox-body-text mb-1.5 last:mb-0">{line}</p>
                    ))}
                    <div className="mt-3 pt-3 border-t border-[#374151] flex flex-wrap items-center gap-2">
                      <span className="chatbox-cta-hint text-xs italic">{welcome.cta}</span>
                      <Link
                        to="/rotta-adriatica"
                        data-testid="chat-welcome-mappa"
                        onClick={() => setOpen(false)}
                        className="inline-flex items-center gap-1 text-xs bg-terracotta text-parchment hover:bg-ink rounded-full px-3 py-1 transition-colors"
                      >
                        <Map size={11}/> Mappa del Mare
                      </Link>
                    </div>
                  </div>
                )}
                {pageCtx.name && (
                  <p
                    data-testid="chat-geo-badge"
                    className="text-center text-[11px] font-mono uppercase tracking-wider text-terracotta mb-2"
                  >
                    📍 Contesto: {pageCtx.name}
                  </p>
                )}
                <p className="font-display italic text-ink2 text-sm text-center mt-2 mb-3">{t.sections.chat_sub}</p>
                <div className="flex flex-wrap gap-2 justify-center" data-testid="chat-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`chat-suggestion-${s.id}`}
                      onClick={() => send(s.message)}
                      className="text-left text-sm text-ink bg-white/80 border border-borderline rounded-full px-3.5 py-2 hover:border-terracotta hover:bg-white transition-colors shadow-sm"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="chat-itinerary-btn"
                  onClick={fillItinerary}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-medium text-parchment bg-ink hover:bg-[#1a1520] border border-gold/40 rounded-xl px-3 py-2.5 transition-colors"
                >
                  <Route size={15} className="text-gold" />
                  🗺️ Genera Itinerario su Misura
                </button>
              </div>
            )}
            {messages.map((m, i) => {
              if (m.role === "assistant" && !m.content) return null; // skip empty placeholder; typing indicator below shows
              return (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl text-sm leading-relaxed ${
                      m.role === "user" ? "bubble-user px-4 py-2.5" : "bubble-ai chatbox-inner-bg"
                    }`}
                  >
                    {m.content.split('\n').map((line, j) => (
                      <p key={j} className={`mb-1 last:mb-0 ${m.role === "assistant" ? "chatbox-body-text" : ""}`}>
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
            {sending && messages[messages.length - 1]?.role === "assistant" && !messages[messages.length - 1]?.content && (
              <div className="flex justify-start">
                <div className="bubble-ai chatbox-inner-bg rounded-2xl text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-terracotta rounded-full animate-pulse" />
                    <span className="w-1.5 h-1.5 bg-terracotta rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                    <span className="w-1.5 h-1.5 bg-terracotta rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                  </span>
                </div>
              </div>
            )}
            {messages.length > 0 && !sending && (
              <button
                type="button"
                data-testid="chat-itinerary-btn-inline"
                onClick={fillItinerary}
                className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-ink2 hover:text-terracotta border border-dashed border-borderline rounded-lg px-2 py-1.5 transition-colors"
              >
                <Route size={12} />
                🗺️ Genera Itinerario su Misura
              </button>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-borderline bg-white flex items-center gap-2"
          >
            <input
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={t.ui.ask_placeholder}
              className="chatbox-input-field flex-1 border border-borderline rounded-full px-4 py-2 text-sm focus:outline-none focus:border-terracotta"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={sending || !input.trim()}
              className="bg-terracotta text-parchment rounded-full p-2.5 disabled:opacity-40 hover:bg-ink transition-colors"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
